import React, { useEffect, useRef, useState } from "react";

const commands = [
  { label: "B", title: "Bold", command: "bold", state: "bold" },
  { label: "I", title: "Italic", command: "italic", state: "italic" },
  { label: "U", title: "Underline", command: "underline", state: "underline" },
  { label: "H2", title: "Heading", command: "formatBlock", value: "h2", state: "h2" },
  { label: "H3", title: "Subheading", command: "formatBlock", value: "h3", state: "h3" },
  { label: "• List", title: "Bulleted list", command: "insertUnorderedList", state: "insertUnorderedList" },
  { label: "1. List", title: "Numbered list", command: "insertOrderedList", state: "insertOrderedList" },
  { label: "❝", title: "Quote", command: "formatBlock", value: "blockquote", state: "blockquote" }
];

export default function RichTextEditor({ value, onChange }) {
  const editor = useRef(null);
  const initialized = useRef(false);
  const savedSelection = useRef(null);
  const [active, setActive] = useState({});

  const saveSelection = () => {
    const selection = window.getSelection();
    if (selection.rangeCount && editor.current?.contains(selection.anchorNode)) savedSelection.current = selection.getRangeAt(0).cloneRange();
  };

  const restoreSelection = () => {
    if (!savedSelection.current) return;
    const selection = window.getSelection();
    selection.removeAllRanges();
    selection.addRange(savedSelection.current);
  };

  const updateToolbar = () => {
    if (!editor.current || !document.activeElement?.isContentEditable) return;
    const block = (document.queryCommandValue("formatBlock") || "").toLowerCase().replace(/[<>]/g, "");
    setActive({
      bold: document.queryCommandState("bold"), italic: document.queryCommandState("italic"), underline: document.queryCommandState("underline"),
      insertUnorderedList: document.queryCommandState("insertUnorderedList"), insertOrderedList: document.queryCommandState("insertOrderedList"),
      h2: block === "h2", h3: block === "h3", blockquote: block === "blockquote"
    });
  };

  useEffect(() => {
    if (editor.current && !initialized.current) {
      editor.current.innerHTML = value || "";
      initialized.current = true;
    }
  }, [value]);

  useEffect(() => {
    const selectionChange = () => { saveSelection(); updateToolbar(); };
    document.addEventListener("selectionchange", selectionChange);
    return () => document.removeEventListener("selectionchange", selectionChange);
  }, []);

  const changed = () => onChange(editor.current?.innerHTML || "");

  const insertLink = () => {
    let url = window.prompt("Enter the link URL");
    if (!url) return;
    url = url.trim();
    if (!/^(https?:\/\/|mailto:|tel:)/i.test(url)) url = "https://" + url;
    editor.current?.focus();
    restoreSelection();
    const selection = window.getSelection();
    const range = selection.rangeCount ? selection.getRangeAt(0) : null;
    if (!range || !editor.current?.contains(range.commonAncestorContainer)) return;
    const link = document.createElement("a");
    link.href = url;
    link.target = "_blank";
    link.rel = "noreferrer";
    if (range.collapsed) link.textContent = url;
    else link.appendChild(range.extractContents());
    range.insertNode(link);
    range.selectNodeContents(link);
    range.collapse(false);
    selection.removeAllRanges();
    selection.addRange(range);
    changed();
  };

  const run = (command, commandValue) => {
    editor.current?.focus();
    restoreSelection();
    if (command === "createLink") insertLink();
    else if (command === "insertImage") { const url = window.prompt("Enter the image URL"); if (url) document.execCommand(command, false, url); }
    else document.execCommand(command, false, commandValue || null);
    changed();
    updateToolbar();
  };

  const handleKeyDown = (event) => {
    if (event.key !== "Enter" || event.shiftKey) return;
    const selection = window.getSelection();
    const node = selection.anchorNode?.nodeType === Node.TEXT_NODE ? selection.anchorNode.parentElement : selection.anchorNode;
    const quote = node?.closest?.("blockquote");
    if (!quote || !editor.current?.contains(quote)) return;
    event.preventDefault();
    const paragraph = document.createElement("p");
    paragraph.innerHTML = "<br>";
    quote.after(paragraph);
    const range = document.createRange();
    range.selectNodeContents(paragraph);
    range.collapse(true);
    selection.removeAllRanges();
    selection.addRange(range);
    changed();
    updateToolbar();
  };

  return <div className="rich-editor"><div className="rich-toolbar" role="toolbar" aria-label="Formatting tools">{commands.map((item) => <button type="button" key={item.title} title={item.title} className={active[item.state] ? "active" : ""} aria-pressed={Boolean(active[item.state])} onMouseDown={(event) => { event.preventDefault(); saveSelection(); }} onClick={() => run(item.command, item.value)}>{item.label}</button>)}<span className="rich-toolbar-divider" /><button type="button" title="Add link" onMouseDown={(event) => { event.preventDefault(); saveSelection(); }} onClick={() => run("createLink")}>Link</button><button type="button" title="Add image" onMouseDown={(event) => { event.preventDefault(); saveSelection(); }} onClick={() => run("insertImage")}>Image</button><button type="button" title="Remove formatting" onMouseDown={(event) => { event.preventDefault(); saveSelection(); }} onClick={() => run("removeFormat")}>Clear</button></div><div ref={editor} className="rich-editor-area" contentEditable="true" suppressContentEditableWarning onInput={changed} onKeyDown={handleKeyDown} onKeyUp={updateToolbar} onMouseUp={() => { saveSelection(); updateToolbar(); }} /><small className="rich-editor-help">Use Shift+Enter for a new line inside a quote. Enter starts a normal paragraph.</small></div>;
}
